import { useCallback, useMemo, memo, useState, useEffect } from 'react'
import { getAmountOptions } from 'utils/loanSelector'
import Select, { ValueTransformer } from 'components/Input/Select'
import { Choice } from 'components/Input/ChoiceGroup'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import { Debtor } from 'interfaces/ApolloResponse.interface'
import { useText } from 'stores/utils/hooks/useText'
import SimpleSelect from 'components/Input/SimpleSelect'
import { isTenantSmava } from 'utils/url'
import FieldDataCollector from '../../FieldDataCollector'
import { PathFormFieldProps } from '../../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../../utils/tracking/amplitude/WithFieldInteraction'
import { useMeta } from '../../../stores/utils/hooks/useMeta'
import { usePatchedField } from '../../../stores/utils/hooks/usePatchedField'

interface OwnProps {
  defaultValue?: number
  choices?: Choice<number>[]
  clue?: string
  debtor?: Debtor
}

type Props = PathFormFieldProps<number> & OwnProps

const SelectInput = observer(
  ({
    path,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelPath,
    clue,
    onTooltip,
    onChange,
    defaultValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    autoComplete,
    ...restProps
  }: Props) => {
    const { input, error } = usePatchedField(path, {
      defaultValue
    })
    const { traversal } = useStores()
    const {
      text: { lists }
    } = useText()

    const {
      removeErrorsForPath,
      handleMultiplePathFormFieldChanges,
      data: {
        common: { purpose }
      }
    } = traversal
    const { placeholder, tooltip, label = '' } = useMeta<number>(path)

    const isMortgageAllowed = (category: string) =>
      category === 'REAL_ESTATE_FINANCING' || category === 'RENOVATION'

    const [selectOptions, setAmountOptions] = useState<Array<Choice<number>>>(
      []
    )

    useEffect(() => {
      const amounts = getAmountOptions({
        amountOptions: lists?.loanSelections,
        loanCategory: purpose,
        isMortgageAllowed: isMortgageAllowed(purpose)
      })
      setAmountOptions(amounts)
    }, [lists, purpose])

    const isSmava = isTenantSmava()

    const changeHandler = useCallback(
      (value: number | undefined) => {
        input.onBlur()

        if (onChange) {
          onChange(value)
        }

        if (path === 'common.amount') {
          handleMultiplePathFormFieldChanges([
            {
              path: 'common.amount',
              value
            },
            {
              path: 'common.purpose',
              value: traversal.data.common.purpose
            },
            {
              path: 'common.vehicle.price',
              value:
                (traversal.data.common.vehicle.initialPayment || 0) +
                (value || 0)
            }
          ])
        }
        input.onChange(value)
        removeErrorsForPath(path)
      },
      [
        input,
        onChange,
        removeErrorsForPath,
        path,
        handleMultiplePathFormFieldChanges,
        traversal.data.common.purpose,
        traversal.data.common.vehicle.initialPayment
      ]
    )

    const blurHandler = useCallback(() => {
      input.onBlur()
    }, [input])
    const focusHandler = useCallback(() => {
      input.onFocus()
    }, [input])

    const selectProps = {
      options: selectOptions,
      clue,
      error,
      label,
      tooltip,
      placeholder,
      ...restProps,
      name: input.name,
      value: `${input.value}`
    }

    const valueTransformer: ValueTransformer<Choice<number>, number> = useMemo(
      () => ({
        selector: 'value',
        extractor: (choice) => choice.value
      }),
      []
    )

    if (isSmava && path === 'common.amount') {
      return (
        <WithFieldInteraction
          trackFocusEvents={false}
          trackOnChange
          path={path}
        >
          <SimpleSelect
            {...selectProps}
            onChange={changeHandler}
            onBlur={blurHandler}
            onFocus={focusHandler}
            onTooltip={onTooltip}
            optionLabel="label"
            valueTransformer={valueTransformer}
          />
        </WithFieldInteraction>
      )
    }

    return (
      <WithFieldInteraction trackFocusEvents={false} trackOnChange path={path}>
        <Select<Choice<number>, number>
          {...selectProps}
          onChange={changeHandler}
          onBlur={blurHandler}
          onFocus={focusHandler}
          onTooltip={onTooltip}
          optionLabel="label"
          valueTransformer={valueTransformer}
        />
      </WithFieldInteraction>
    )
  }
)

function PathSelect(props: Props) {
  const { path, labelPath } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <SelectInput {...props} />
    </FieldDataCollector>
  )
}

export default memo(PathSelect)
